import React, { useState, useEffect, useRef } from "react";
import { Handle, Position, NodeProps, useReactFlow, useNodes } from "reactflow";
import { Trash2, Plus, BetweenHorizontalEnd, FlaskConical } from "lucide-react";
import { Input } from "@components/ui/input";
import { Button } from "@components/ui/button";
import { Switch } from "@components/ui/switch";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import Confirmation from "@components/common/ConfirmDialog";
import { toast } from "@components/ui/use-toast";
import { getConnectedInputs, getExecutionPath } from "../common/nodeDataFlow";
import { handlerStyle, unwrapJsonData } from "../common/commonData";
import PaginatedInputViewer from "../common/PaginatedInputViewer";
import ResponseViewer from "../common/ResponseViewer";
import { executeWorkflow } from "../createPluginApis";
import {
  getNodeExecutionParameters,
  buildWorkflowPayload,
  handleNodeExecutionResults,
  findDownstreamNodes,
} from "../utils/nodeExecutionUtils";
import { getNodeBackendType } from "../utils/nodeTransformUtils";
import CollapsedNodeView from "../common/NodeHeaderView";
import NodeContextMenu, {
  useNodeContextMenu,
  createContextMenuItems,
} from "../common/NodeContextMenu";
import NodeContentSection from "../common/NodeContentSection";
import { FullscreenWrapper } from "../common/withFullscreen";
import { FullscreenToggleButton } from "../common/FullscreenToggleButton";
import { ValidatedTitleInput } from "../components/ValidatedTitleInput";
import { useResizablePanels } from "../hooks/useResizablePanels";
import { PanelDivider } from "../common/PanelDivider";

interface AggregateNodeData {
  label?: string;
  uid: string;
  title: string;
  description?: string;
  aggregate?: "aggregateIndividualFields" | "aggregateAllItemData";
  fieldsToAggregate?: Array<{
    fieldToAggregate: string;
    renameField: boolean;
    outputFieldName: string;
  }>;
  destinationFieldName?: string;
  include?: "allFields" | "specifiedFields" | "allFieldsExcept";
  fieldsToInclude?: string;
  fieldsToExclude?: string;
  options?: {
    keepMissing?: boolean;
    mergeLists?: boolean;
    disableDotNotation?: boolean;
  };
  onDelete?: (id: string) => void;
  onDataChange?: (id: string, updates: any) => void;
  borderColor?: string;
  shadowColor?: string;
}

const AggregateNode: React.FC<NodeProps<AggregateNodeData>> = ({
  id,
  data,
}) => {
  const label = data.label || "Aggregate";
  const [uid, setUid] = useState(data.uid || "");
  const [title, setTitle] = useState(data.title || "");
  const [description, setDescription] = useState(data.description || "");
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const nodes = useNodes();

  // Context menu
  const {
    showContextMenu,
    contextMenuPosition,
    handleContextMenu,
    closeContextMenu,
  } = useNodeContextMenu(id);

  // Aggregate specific state
  const [aggregate, setAggregate] = useState<
    "aggregateIndividualFields" | "aggregateAllItemData"
  >(data.aggregate || "aggregateAllItemData");

  const [fieldsToAggregate, setFieldsToAggregate] = useState<
    Array<{
      fieldToAggregate: string;
      renameField: boolean;
      outputFieldName: string;
    }>
  >(() => {
    // Handle both frontend format (array) and backend format (object with fieldToAggregate property)
    if (Array.isArray(data.fieldsToAggregate)) {
      return data.fieldsToAggregate;
    }
    return [{ fieldToAggregate: "", renameField: false, outputFieldName: "" }];
  });

  const [destinationFieldName, setDestinationFieldName] = useState<string>(
    data.destinationFieldName || "",
  );

  const [include, setInclude] = useState<
    "allFields" | "specifiedFields" | "allFieldsExcept"
  >(data.include || "allFields");

  const [fieldsToInclude, setFieldsToInclude] = useState<string>(
    data.fieldsToInclude || "",
  );

  const [fieldsToExclude, setFieldsToExclude] = useState<string>(
    data.fieldsToExclude || "",
  );

  const [options, setOptions] = useState<{
    keepMissing?: boolean;
    mergeLists?: boolean;
    disableDotNotation?: boolean;
  }>(data.options || {});

  const [showOptionsDropdown, setShowOptionsDropdown] = useState(false);

  // Input/Output state
  const [inputData, setInputData] = useState<any[]>([]);
  const [outputData, setOutputData] = useState<any>(null);
  const [errorResponse, setErrorResponse] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [isTitleValid, setIsTitleValid] = useState(true);

  const reactFlowInstance = useReactFlow();
  const { getNodes, getEdges } = useReactFlow();
  const { containerRef, widths, startDrag } = useResizablePanels([
    33.33, 33.33, 33.34,
  ]);
  const hasLoadedStoredParams = useRef(false);

  // Auto-generate UID if not present
  useEffect(() => {
    if (!uid) {
      const newUid = `aggregate_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      setUid(newUid);
    }
  }, []);

  // Load saved parameters from data prop (when loading from backend)
  useEffect(() => {
    if (hasLoadedStoredParams.current) return;

    const params = (data as any).storedParameters || {};

    if (params.aggregate) {
      setAggregate(params.aggregate);
    }

    if (params.fieldsToAggregate?.fieldToAggregate) {
      // Ensure fieldsToAggregate is an array of objects with correct structure
      const loadedFields = Array.isArray(
        params.fieldsToAggregate.fieldToAggregate,
      )
        ? params.fieldsToAggregate.fieldToAggregate.map((item: any) => {
            if (
              typeof item === "object" &&
              item.fieldToAggregate !== undefined
            ) {
              // Already in correct format
              return {
                fieldToAggregate: item.fieldToAggregate || "",
                renameField: item.renameField || false,
                outputFieldName: item.outputFieldName || "",
              };
            } else if (typeof item === "string") {
              // Convert string to object format
              return {
                fieldToAggregate: item,
                renameField: false,
                outputFieldName: "",
              };
            }
            return {
              fieldToAggregate: "",
              renameField: false,
              outputFieldName: "",
            };
          })
        : [{ fieldToAggregate: "", renameField: false, outputFieldName: "" }];

      setFieldsToAggregate(
        loadedFields.length > 0
          ? loadedFields
          : [{ fieldToAggregate: "", renameField: false, outputFieldName: "" }],
      );
    }
    if (params.destinationFieldName !== undefined) {
      setDestinationFieldName(params.destinationFieldName || "");
    }
    if (params.include) {
      setInclude(params.include);
    }
    if (params.fieldsToInclude !== undefined) {
      setFieldsToInclude(params.fieldsToInclude || "");
    }
    if (params.fieldsToExclude !== undefined) {
      setFieldsToExclude(params.fieldsToExclude || "");
    }
    if (params.options) {
      setOptions(params.options);
    }
    hasLoadedStoredParams.current = true;
  }, [(data as any).storedParameters]);

  const storedParameters = React.useMemo(
    () => ({
      aggregate,
      fieldsToAggregate:
        aggregate === "aggregateIndividualFields"
          ? {
              fieldToAggregate: fieldsToAggregate.filter((f) =>
                f?.fieldToAggregate?.trim(),
              ),
            }
          : null,
      destinationFieldName,
      include,
      fieldsToInclude: include === "specifiedFields" ? fieldsToInclude : "",
      fieldsToExclude: include === "allFieldsExcept" ? fieldsToExclude : "",
      options: {
        keepMissing: options?.keepMissing ?? false,
        mergeLists: options?.mergeLists ?? false,
        disableDotNotation: options?.disableDotNotation ?? false,
      },
    }),
    [
      aggregate,
      fieldsToAggregate,
      destinationFieldName,
      include,
      fieldsToInclude,
      fieldsToExclude,
      options,
    ],
  );

  React.useEffect(() => {
    if (!data.onDataChange) return;

    data.onDataChange(id, {
      uid,
      title,
      description,
      aggregate,
      fieldsToAggregate,
      destinationFieldName,
      include,
      fieldsToInclude,
      fieldsToExclude,
      options,
      outputData,
      response: outputData,
      sub_type: "aggregate",
      type: "data_transformation",
      storedParameters,
    });
  }, [
    uid,
    title,
    description,
    aggregate,
    fieldsToAggregate,
    destinationFieldName,
    include,
    fieldsToInclude,
    fieldsToExclude,
    options,
    outputData,
    storedParameters,
  ]);

  // Refresh input data when node connections change
  useEffect(() => {
    refreshInputData();
    // Only refresh every 3 seconds to avoid performance issues
    const interval = setInterval(refreshInputData, 3000);
    return () => clearInterval(interval);
  }, [id]); // Only re-run if node ID changes

  // Sync state to parent with storedParameters
  useEffect(() => {
    if (!data.onDataChange) return;

    // Build storedParameters for backend
    const storedParameters: any = {
      aggregate,
      fieldsToAggregate:
        aggregate === "aggregateIndividualFields"
          ? {
              fieldToAggregate: Array.isArray(fieldsToAggregate)
                ? fieldsToAggregate.filter((f) => f?.fieldToAggregate?.trim())
                : [],
            }
          : null,
      destinationFieldName: destinationFieldName || "",
      include,
      fieldsToInclude:
        include === "specifiedFields" ? fieldsToInclude || "" : "",
      fieldsToExclude:
        include === "allFieldsExcept" ? fieldsToExclude || "" : "",
      options: {
        keepMissing: options?.keepMissing ?? false,
        mergeLists: options?.mergeLists ?? false,
        disableDotNotation: options?.disableDotNotation ?? false,
      },
    };

    data.onDataChange(id, {
      uid,
      title,
      description,
      aggregate,
      fieldsToAggregate,
      destinationFieldName,
      include,
      fieldsToInclude,
      fieldsToExclude,
      options,
      outputData,
      sub_type: "aggregate",
      type: "data_transformation",
      storedParameters,
    });
  }, [
    uid,
    title,
    description,
    aggregate,
    fieldsToAggregate,
    destinationFieldName,
    include,
    fieldsToInclude,
    fieldsToExclude,
    options,
    outputData,
  ]);

  // Ref to track previous outputData for sync effect
  const prevOutputDataRef = useRef<any>(null);

  // Sync outputData from node data (updated during workflow execution)
  // Use deep comparison to prevent unnecessary updates
  useEffect(() => {
    const newOutputData = (data as any).outputData || (data as any).response;

    // Skip if no new data
    if (!newOutputData) {
      // Clear output if explicitly set to null/undefined
      if (outputData !== null && newOutputData === null) {
        prevOutputDataRef.current = null;
        setOutputData(null);
      }
      return;
    }

    // Skip if it's the same reference (optimization)
    if (prevOutputDataRef.current === newOutputData) {
      return;
    }

    // Deep comparison - only update if stringified content changed
    const newDataStr = JSON.stringify(newOutputData);
    const prevDataStr = prevOutputDataRef.current
      ? JSON.stringify(prevOutputDataRef.current)
      : null;

    if (newDataStr !== prevDataStr) {
      prevOutputDataRef.current = newOutputData;
      setOutputData(newOutputData);
    } else {
      // Content is the same, just update the reference to prevent future comparisons
      prevOutputDataRef.current = newOutputData;
    }
  }, [(data as any).outputData, (data as any).response]);

  // Sync error from node data (set during global workflow execution)
  useEffect(() => {
    const executionStatus = (data as any).executionStatus;
    const error = (data as any).error;

    // If node failed during global execution, show error in ResponseViewer
    if (executionStatus === "failed" && error) {
      setErrorResponse(error);
    } else if (executionStatus === "success") {
      // Clear error when node succeeds
      setErrorResponse(null);
    }
  }, [(data as any).executionStatus, (data as any).error]);

  const refreshInputData = () => {
    const nodes = getNodes();
    const edges = getEdges();
    const connectedInputs = getConnectedInputs(id, edges, nodes);

    // Only update if the data actually changed (prevent unnecessary re-renders)
    setInputData((prevData) => {
      if (JSON.stringify(prevData) === JSON.stringify(connectedInputs)) {
        return prevData; // No change, return previous to avoid re-render
      }
      return connectedInputs;
    });
  };

  const handleDelete = (nodeId: string) => {
    setDeleteId(nodeId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (deleteId && data.onDelete) {
      data.onDelete(deleteId);
    }
    setShowDeleteConfirm(false);
    setDeleteId(null);
  };

  const handleDrop = (e: React.DragEvent, fieldIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    const dragData = e.dataTransfer.getData("application/json");
    if (dragData) {
      try {
        const parsedData = JSON.parse(dragData);
        const fieldPath = parsedData.path;

        if (fieldPath) {
          // Extract field name from path
          // Strip {{ }} wrappers first
          let cleanPath = fieldPath
            .replace(/^\{\{\s*/, "")
            .replace(/\s*\}\}$/, "");
          // Remove $json. or config. prefix
          cleanPath = cleanPath
            .replace(/^\$json\./, "")
            .replace(/^config\./, "");
          // Get last segment of the path
          const pathParts = cleanPath.split(".");
          const fieldName = pathParts[pathParts.length - 1].replace(
            /\[\d+\]/g,
            "",
          );

          // Update the field at the specific index
          const newFields = [...fieldsToAggregate];
          newFields[fieldIndex] = {
            ...newFields[fieldIndex],
            fieldToAggregate: fieldName,
          };
          setFieldsToAggregate(newFields);
        }
      } catch (error) {
        console.error("Error parsing drag data:", error);
      }
    }
  };

  const updateFieldValue = (
    index: number,
    field: "fieldToAggregate" | "outputFieldName",
    value: string,
  ) => {
    if (!Array.isArray(fieldsToAggregate)) return;
    const newFields = [...fieldsToAggregate];
    newFields[index] = {
      ...newFields[index],
      [field]: value,
    };
    setFieldsToAggregate(newFields);
  };

  const toggleRenameField = (index: number) => {
    if (!Array.isArray(fieldsToAggregate)) return;
    const newFields = [...fieldsToAggregate];
    newFields[index] = {
      ...newFields[index],
      renameField: !newFields[index].renameField,
    };
    setFieldsToAggregate(newFields);
  };

  const addField = () => {
    if (!Array.isArray(fieldsToAggregate)) {
      setFieldsToAggregate([
        { fieldToAggregate: "", renameField: false, outputFieldName: "" },
      ]);
      return;
    }
    setFieldsToAggregate([
      ...fieldsToAggregate,
      { fieldToAggregate: "", renameField: false, outputFieldName: "" },
    ]);
  };

  const removeField = (index: number) => {
    if (!Array.isArray(fieldsToAggregate)) return;
    const newFields = fieldsToAggregate.filter((_, i) => i !== index);
    // Keep at least one field
    if (newFields.length === 0) {
      setFieldsToAggregate([
        { fieldToAggregate: "", renameField: false, outputFieldName: "" },
      ]);
    } else {
      setFieldsToAggregate(newFields);
    }
  };

  const addOption = (
    optionType: "keepMissing" | "mergeLists" | "disableDotNotation",
  ) => {
    if (optionType === "keepMissing") {
      setOptions({ ...options, keepMissing: false });
    } else if (optionType === "mergeLists") {
      setOptions({ ...options, mergeLists: false });
    } else if (optionType === "disableDotNotation") {
      setOptions({ ...options, disableDotNotation: false });
    }
    setShowOptionsDropdown(false);
  };

  const removeOption = (
    optionType: "keepMissing" | "mergeLists" | "disableDotNotation",
  ) => {
    const newOptions = { ...options };
    delete newOptions[optionType];
    setOptions(newOptions);
  };

  // Execute workflow
  const executeAggregate = async () => {
    if (!inputData?.length) {
      toast({
        variant: "destructive",
        title: "No Input Data",
        description: "Connect at least one node to provide input data",
      });
      return;
    }

    if (!title.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Title is required for the aggregate node",
        duration: 3000,
      });
      return;
    }

    // Get active mesh flow from sessionStorage
    const activeMeshFlowStr = sessionStorage.getItem("activeMeshFlow");
    if (!activeMeshFlowStr) {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          "No active mesh flow. Please create or select a mesh flow first.",
        duration: 4000,
      });
      return;
    }

    setOutputData(null);
    setErrorResponse(null);
    setLoading(true);

    try {
      const activeMeshFlow = JSON.parse(activeMeshFlowStr);
      const pluginId = sessionStorage.getItem("editPluginId") || "";

      // Get all nodes and edges from React Flow
      const allNodes = reactFlowInstance.getNodes();
      const allEdges = reactFlowInstance.getEdges();

      // Get execution path: only nodes from start to current node (no downstream nodes)
      const executableNodes = getExecutionPath(id, allNodes, allEdges);

      // Mark all executable nodes as pending
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          // Check if this node is in the execution path
          const isExecutable = executableNodes.some(
            (n: any) => n.id === node.id,
          );
          if (isExecutable) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "pending",
                executionResult: undefined,
                error: undefined,
              },
            };
          }
          return node;
        }),
      );

      // Build nodes array for workflow
      const workflowNodes = executableNodes.map((node) => {
        let nodeType = "core";
        let subType = "http_request";
        let parameters: any = {};

        if (node.id === id) {
          // This is the current AggregateNode
          const nodeParameters: any = {
            aggregate,
            fieldsToAggregate:
              aggregate === "aggregateIndividualFields"
                ? {
                    fieldToAggregate: Array.isArray(fieldsToAggregate)
                      ? fieldsToAggregate.filter((f) =>
                          f?.fieldToAggregate?.trim(),
                        )
                      : [],
                  }
                : null,
            destinationFieldName: destinationFieldName || "",
            include,
            fieldsToInclude:
              include === "specifiedFields" ? fieldsToInclude || "" : "",
            fieldsToExclude:
              include === "allFieldsExcept" ? fieldsToExclude || "" : "",
            options: {
              keepMissing: options.keepMissing ?? false,
              mergeLists: options.mergeLists ?? false,
              disableDotNotation: options.disableDotNotation ?? false,
            },
          };

          nodeType = "data_transformation";
          subType = "aggregate";
          parameters = {
            ...nodeParameters,
            label: label,
          };
        } else {
          // Other nodes - use centralized mapping
          const backendTypeInfo = getNodeBackendType(node.type || "custom");
          nodeType = backendTypeInfo.type;
          subType = backendTypeInfo.sub_type;

          // Override type/subtype if explicitly set in node data
          if (node.data.configData) {
            // Authentication/configuration nodes already have correct type from mapping
          }
          if (node.data.sub_type) {
            subType = node.data.sub_type;
            nodeType = node.data.type || "core";
          }

          // Get parameters using centralized utility
          parameters = getNodeExecutionParameters(node, pluginId).parameters;
        }

        return {
          uid: node.data.uid || node.id,
          title: node.data.title || node.data.label || "Untitled",
          type: nodeType,
          sub_type: subType,
          version: "1.0",
          position: [node.position.x, node.position.y],
          parameters,
        };
      });

      // Create a mapping from React Flow node IDs to UIDs
      const nodeIdToUidMap: Record<string, string> = {};
      executableNodes.forEach((node) => {
        const nodeUid = node.data.uid || node.id;
        nodeIdToUidMap[node.id] = nodeUid;
      });

      // Create Set of node IDs in execution path for filtering
      const executionPathNodeIds = new Set(executableNodes.map((n) => n.id));

      // Build connections object from edges using UIDs (only between nodes in execution path)
      const connections: any = {};
      allEdges
        .filter(
          (edge) =>
            executionPathNodeIds.has(edge.source) &&
            executionPathNodeIds.has(edge.target),
        )
        .forEach((edge) => {
          const sourceUid = nodeIdToUidMap[edge.source];
          const targetUid = nodeIdToUidMap[edge.target];

          // Only include connections between executable nodes
          if (!sourceUid || !targetUid) return;

          if (!connections[sourceUid]) {
            connections[sourceUid] = {
              main: [],
            };
          }

          if (!connections[sourceUid].main[0]) {
            connections[sourceUid].main[0] = [];
          }

          connections[sourceUid].main[0].push({
            node: targetUid,
            type: "main",
            index: 0,
          });
        });

      // Build complete workflow payload
      const workflowPayload = buildWorkflowPayload(
        activeMeshFlow,
        workflowNodes,
        connections,
      );

      const result = await executeWorkflow(workflowPayload);

      // Update ALL executed nodes with their results (not just the clicked node)
      if (result?.results?.[0]?.node_results) {
        const nodeResults = result.results[0].node_results;

        // Use the utility function to handle node execution results
        const { hasErrors, errorMessage, errorNodeTitle } =
          handleNodeExecutionResults(reactFlowInstance, nodeResults, allEdges);

        // Extract this node's output for local state
        const resultsMap = new Map<string, any>();
        nodeResults.forEach((nodeResult: any) => {
          resultsMap.set(nodeResult.uid, nodeResult);
        });

        const currentNodeResult = resultsMap.get(uid || id);
        if (currentNodeResult) {
          // Check if this node failed
          const isFailed =
            currentNodeResult.status === "error" ||
            currentNodeResult.status === "failed" ||
            currentNodeResult.status === "failure";

          if (isFailed) {
            // Show error in ResponseViewer but don't pass to downstream nodes
            setErrorResponse(currentNodeResult.error || "Execution failed");
            setOutputData(null);
          } else if (currentNodeResult.output_data) {
            // Success - extract and display output data
            const responseData = unwrapJsonData(currentNodeResult.output_data);
            setOutputData(responseData);
            setErrorResponse(null);
          }
        }

        // Show appropriate toast based on execution result
        if (hasErrors) {
          // Show error toast with specific error message
          const errorDescription = errorMessage
            ? `${errorNodeTitle}: ${errorMessage}`
            : "One or more nodes failed during execution";

          const shortError =
            errorDescription.length > 150
              ? errorDescription.substring(0, 150) + "..."
              : errorDescription;

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description: shortError,
            duration: 7000,
          });
        } else {
          // Only show success if ALL nodes succeeded
          toast({
            variant: "success",
            title: "Success",
            description: "Aggregate executed successfully",
            duration: 3000,
          });
        }
      } else {
        // Fallback - no node results in response
        // Check for success at top level
        const isSuccess =
          result?.success === true ||
          result?.status === "success" ||
          result?.body?.status === 1 ||
          result?.body?.status === "1" ||
          result?.status === 1;

        if (isSuccess) {
          // Mark current node as success
          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "success",
                  },
                };
              }
              return node;
            }),
          );

          toast({
            variant: "success",
            title: "Success",
            description: result?.message || "Aggregate executed successfully",
            duration: 3000,
          });
        } else {
          // Mark current node and all downstream nodes as failed
          const allEdges = reactFlowInstance.getEdges();
          const downstreamNodes = findDownstreamNodes(id, allEdges);

          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error:
                      result?.body?.message ||
                      result?.message ||
                      "Execution failed",
                  },
                };
              } else if (downstreamNodes.has(node.id)) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error: "Not executed due to upstream failure",
                  },
                };
              }
              return node;
            }),
          );

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description:
              result?.body?.message ||
              result?.message ||
              "Aggregate execution failed",
            duration: 4000,
          });
        }
      }
    } catch (error: any) {
      console.error("Aggregate Execution Error:", error);
      setErrorResponse(error?.message || "Aggregate execution failed");
      setOutputData(null);

      // Get all edges for finding downstream nodes
      const allEdges = reactFlowInstance.getEdges();
      const downstreamNodes = findDownstreamNodes(id, allEdges);

      // Update current node and all downstream nodes to failed status
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            // Mark the failed node as failed
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: error?.message || "Aggregate execution failed",
              },
            };
          } else if (downstreamNodes.has(node.id)) {
            // Mark downstream nodes as failed (not executed due to upstream failure)
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: "Not executed due to upstream failure",
              },
            };
          }
          return node;
        }),
      );

      toast({
        variant: "destructive",
        title: "Error",
        description: error?.message || "Aggregate execution failed",
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Copy output to clipboard
  const handleCopyOutput = () => {
    const dataToCopy = errorResponse || outputData;
    if (!dataToCopy) return;
    navigator.clipboard.writeText(JSON.stringify(dataToCopy, null, 2));
    toast({
      variant: "success",
      title: "Copied!",
      description: "Output copied to clipboard.",
      duration: 2000,
    });
  };

  // Get display data for InputViewer - prefer response data, fallback to configData
  const getDisplayData = (input: any) => {
    // Helper to unwrap json_data from backend format
    const extractJsonData = (data: any): any => {
      if (!data) return data;

      if (Array.isArray(data)) {
        return data.map((item: any) => {
          if (item && typeof item === "object" && "json_data" in item) {
            return item.json_data;
          }
          return item;
        });
      }

      if (data && typeof data === "object" && "json_data" in data) {
        return data.json_data;
      }

      return data;
    };

    // If response exists and has data, use it
    if (input.response && Object.keys(input.response).length > 0) {
      return extractJsonData(input.response);
    }

    // Check outputData
    if (input.outputData && Object.keys(input.outputData).length > 0) {
      return extractJsonData(input.outputData);
    }

    // Otherwise, use filtered configData
    const displayData: any = {};
    const EXCLUDE = [
      "uid",
      "title",
      "description",
      "label",
      "position",
      "nodeType",
      "isDecript",
      "isDecrypted",
      "testConnectionBtn",
      "tabId",
      "configType",
      "onDelete",
      "onDataChange",
      "header",
      "borderColor",
      "shadowColor",
    ];

    if (input.configData) {
      Object.entries(input.configData)
        .filter(([key]) => !EXCLUDE.includes(key))
        .forEach(([key, value]) => (displayData[key] = value));
    }

    return displayData;
  };

  // Render input data section with pagination
  const renderInputData = () => {
    if (!inputData?.length) {
      return (
        <div className="text-xs text-muted-foreground p-4 border rounded-md bg-muted/20 text-center">
          <div className="font-medium">No Input Connected</div>
          <div>Connect nodes to see data here</div>
        </div>
      );
    }

    // Build sources array for PaginatedInputViewer (handles tabs internally)
    const sources = inputData.map((input, index) => {
      // Generate label: if from switch node, use "Rule X", otherwise use original label
      let displayLabel = input.label || `Source ${index + 1}`;

      // Check if label indicates switch node (e.g., "switch", "SWITCH")
      if (displayLabel.toLowerCase().includes("switch")) {
        displayLabel = `Rule ${index + 1}`;
      }

      return {
        label: displayLabel,
        data: getDisplayData(input),
        isConfig: !!input.configData, // Mark as config if source is configuration
      };
    });

    return (
      <PaginatedInputViewer
        sources={sources}
        onRefresh={refreshInputData}
        sourceCount={inputData.length}
      />
    );
  };

  return (
    <FullscreenWrapper
      id={id}
      isExpanded={isExpanded}
      executionStatus={(data as any).executionStatus}
      borderColor={data.borderColor}
      shadowColor={data.shadowColor}
      onContextMenu={handleContextMenu}
      expandedClassName="w-[90vw] !max-w-[1000px] flex flex-col"
    >
      <Confirmation
        text="Are you sure you want to delete this node?"
        confirmAction={confirmDelete}
        rejectAction={() => setShowDeleteConfirm(false)}
        confirmText="Confirm"
        rejectText="Cancel"
        show={showDeleteConfirm}
        closeDialog={() => setShowDeleteConfirm(false)}
      />

      {/* Context Menu */}
      <NodeContextMenu
        show={showContextMenu}
        position={contextMenuPosition}
        onClose={closeContextMenu}
        items={[
          createContextMenuItems.expandCollapse(isExpanded, () =>
            setIsExpanded((prev) => !prev),
          ),
          createContextMenuItems.delete(
            () => handleDelete(id),
            !!data.onDelete,
          ),
        ]}
      />

      {/* Handles */}
      <Handle
        id="input"
        type="target"
        position={Position.Left}
        className={handlerStyle}
      />
      <Handle
        id="output"
        type="source"
        position={Position.Right}
        className={handlerStyle}
      />

      {/* Header */}
      <div
        className={`flex items-center justify-between gap-4 rounded-md transition-all duration-500 flex-shrink-0 ${
          isExpanded ? "p-3" : "!p-3"
        }`}
      >
        <CollapsedNodeView
          icon={
            <BetweenHorizontalEnd
              className={
                (data as any).executionStatus === "running" ||
                (data as any).executionStatus === "pending"
                  ? "animate-bounce-horizontal"
                  : ""
              }
            />
          }
          label={label}
          title={title}
          iconSize={35}
          borderColor={data.borderColor}
          onToggle={() => setIsExpanded((prev) => !prev)}
          isExpanded={isExpanded}
          executionStatus={(data as any).executionStatus}
          description={description}
        />
        <FullscreenToggleButton isExpanded={isExpanded} />
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* Metadata Form */}
          <div className="mt-2 p-4 border-t flex items-start gap-4 flex-shrink-0">
            <div className="space-y-1 w-1/2">
              <label className="text-xs font-medium text-muted-foreground">
                Title <span className="text-primary">*</span>
              </label>
              <ValidatedTitleInput
                id={id}
                title={title}
                onChange={setTitle}
                nodes={nodes}
                dataTitle={data.title}
                onValidationChange={setIsTitleValid}
              />
            </div>
            <div className="space-y-1 w-1/2">
              <label className="text-xs font-medium text-muted-foreground">
                Description (optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter node description"
                className="w-full p-2 border rounded text-xs resize-none text-muted-foreground bg-background"
                rows={2}
              />
            </div>
          </div>

          {/* 3 Section Grid */}
          <div
            ref={containerRef}
            className="flex gap-0 p-4 border-t flex-1 min-h-0 overflow-hidden"
          >
            {/* Left Section - Input Data (4/12 columns) */}
            <div
              style={{ width: `${widths[0]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection scrollable={true} hasRelative={true}>
                {renderInputData()}
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(0)} />

            {/* Middle Section - Parameters (4/12 columns) */}
            <div
              style={{ width: `${widths[1]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection hasRelative={true} scrollable={false}>
                {/* Single scroll surface — wraps all scrollable content */}
                <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col gap-2">
                  <h3 className="text-sm font-semibold sticky top-0 bg-background z-10 pb-2 border-b flex-shrink-0">
                    Parameters
                  </h3>

                  {/* Aggregate Type */}
                  <div className="space-y-2 border rounded-md p-3 bg-muted/20">
                    <label className="text-xs font-semibold">
                      Aggregate Type
                    </label>
                    <Select
                      value={aggregate}
                      onValueChange={(value: any) => setAggregate(value)}
                    >
                      <SelectTrigger className="w-full text-xs h-8">
                        <SelectValue placeholder="Select aggregate type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem
                            value="aggregateAllItemData"
                            className="text-xs"
                          >
                            Aggregate All Item Data
                          </SelectItem>
                          <SelectItem
                            value="aggregateIndividualFields"
                            className="text-xs"
                          >
                            Aggregate Individual Fields
                          </SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Individual Fields - Only show when aggregateIndividualFields */}
                  {aggregate === "aggregateIndividualFields" &&
                    Array.isArray(fieldsToAggregate) && (
                      <div className="space-y-2 border rounded-md p-3 bg-muted/20">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-semibold">
                            Fields To Aggregate
                          </label>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2"
                            onClick={addField}
                          >
                            <Plus size={14} className="mr-1" />
                            Add Field
                          </Button>
                        </div>
                        <p className="text-[10px] text-muted-foreground">
                          Drag fields from input or type field names
                        </p>

                        <div className="space-y-2">
                          {fieldsToAggregate.map((field, index) => (
                            <div
                              key={index}
                              className="border rounded-md p-2 bg-background space-y-2"
                            >
                              <div className="flex items-center gap-2">
                                <div
                                  onDragOver={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    e.currentTarget.classList.add(
                                      "ring-2",
                                      "ring-primary",
                                      "bg-primary/5",
                                    );
                                  }}
                                  onDragLeave={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    e.currentTarget.classList.remove(
                                      "ring-2",
                                      "ring-primary",
                                      "bg-primary/5",
                                    );
                                  }}
                                  onDrop={(e) => {
                                    e.currentTarget.classList.remove(
                                      "ring-2",
                                      "ring-primary",
                                      "bg-primary/5",
                                    );
                                    handleDrop(e, index);
                                  }}
                                  className="flex-1 border border-transparent rounded transition-all"
                                >
                                  <Input
                                    value={field.fieldToAggregate}
                                    onChange={(e) =>
                                      updateFieldValue(
                                        index,
                                        "fieldToAggregate",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Drag or type field name"
                                    className="text-xs h-7 font-mono"
                                  />
                                </div>
                                <Trash2
                                  size={16}
                                  className="cursor-pointer text-red-500 hover:text-red-700 transition flex-shrink-0"
                                  onClick={() => removeField(index)}
                                />
                              </div>

                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={field.renameField}
                                  onCheckedChange={() =>
                                    toggleRenameField(index)
                                  }
                                />
                                <label className="text-[10px] text-muted-foreground">
                                  Rename this field in output
                                </label>
                              </div>

                              {field.renameField && (
                                <Input
                                  value={field.outputFieldName}
                                  onChange={(e) =>
                                    updateFieldValue(
                                      index,
                                      "outputFieldName",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="Output field name"
                                  className="text-xs h-7 font-mono"
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {/* Destination Field Name */}
                  <div className="space-y-2 border rounded-md p-3 bg-muted/20">
                    <label className="text-xs font-semibold">
                      Destination Field Name (Optional)
                    </label>
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.classList.add(
                          "ring-2",
                          "ring-primary",
                          "bg-primary/5",
                        );
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.classList.remove(
                          "ring-2",
                          "ring-primary",
                          "bg-primary/5",
                        );
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.classList.remove(
                          "ring-2",
                          "ring-primary",
                          "bg-primary/5",
                        );
                        const dragData =
                          e.dataTransfer.getData("application/json");
                        if (dragData) {
                          try {
                            const parsedData = JSON.parse(dragData);
                            const fieldPath = parsedData.path;
                            if (fieldPath) {
                              let cleanPath = fieldPath
                                .replace(/^\{\{\s*/, "")
                                .replace(/\s*\}\}$/, "");
                              cleanPath = cleanPath
                                .replace(/^\$json\./, "")
                                .replace(/^config\./, "");
                              const pathParts = cleanPath.split(".");
                              const fieldName = pathParts[
                                pathParts.length - 1
                              ].replace(/\[\d+\]/g, "");
                              setDestinationFieldName(fieldName);
                            }
                          } catch (error) {
                            console.error("Error parsing drag data:", error);
                          }
                        }
                      }}
                      className="w-full border border-transparent rounded transition-all"
                    >
                      <Input
                        value={destinationFieldName}
                        onChange={(e) =>
                          setDestinationFieldName(e.target.value)
                        }
                        placeholder="Drag field here or type field name"
                        className="text-xs h-7 font-mono"
                      />
                    </div>
                  </div>

                  {/* Include Mode */}
                  <div className="space-y-2 border rounded-md p-3 bg-muted/20">
                    <label className="text-xs font-semibold">
                      Include Fields
                    </label>
                    <Select
                      value={include}
                      onValueChange={(value: any) => setInclude(value)}
                    >
                      <SelectTrigger className="w-full text-xs h-8">
                        <SelectValue placeholder="Select include mode" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="allFields" className="text-xs">
                            All Fields
                          </SelectItem>
                          <SelectItem
                            value="specifiedFields"
                            className="text-xs"
                          >
                            Specified Fields Only
                          </SelectItem>
                          <SelectItem
                            value="allFieldsExcept"
                            className="text-xs"
                          >
                            All Except Specified
                          </SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>

                    {include === "specifiedFields" && (
                      <div className="space-y-1 mt-2">
                        <label className="text-[10px] text-muted-foreground">
                          Fields to include (comma-separated or drag fields)
                        </label>
                        <div
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            e.currentTarget.classList.add(
                              "ring-2",
                              "ring-primary",
                              "bg-primary/5",
                            );
                          }}
                          onDragLeave={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            e.currentTarget.classList.remove(
                              "ring-2",
                              "ring-primary",
                              "bg-primary/5",
                            );
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            e.currentTarget.classList.remove(
                              "ring-2",
                              "ring-primary",
                              "bg-primary/5",
                            );
                            const dragData =
                              e.dataTransfer.getData("application/json");
                            if (dragData) {
                              try {
                                const parsedData = JSON.parse(dragData);
                                const fieldPath = parsedData.path;
                                if (fieldPath) {
                                  let cleanPath = fieldPath
                                    .replace(/^\{\{\s*/, "")
                                    .replace(/\s*\}\}$/, "");
                                  cleanPath = cleanPath
                                    .replace(/^\$json\./, "")
                                    .replace(/^config\./, "");
                                  const pathParts = cleanPath.split(".");
                                  const fieldName = pathParts[
                                    pathParts.length - 1
                                  ].replace(/\[\d+\]/g, "");
                                  const existingFields = fieldsToInclude
                                    .split(",")
                                    .map((f) => f.trim())
                                    .filter((f) => f.length > 0);
                                  if (!existingFields.includes(fieldName)) {
                                    existingFields.push(fieldName);
                                  }
                                  setFieldsToInclude(existingFields.join(", "));
                                }
                              } catch (error) {
                                console.error(
                                  "Error parsing drag data:",
                                  error,
                                );
                              }
                            }
                          }}
                          className="w-full border border-transparent rounded transition-all"
                        >
                          <Input
                            value={fieldsToInclude}
                            onChange={(e) => setFieldsToInclude(e.target.value)}
                            placeholder="field1,field2,field3"
                            className="text-xs h-7 font-mono"
                          />
                        </div>
                      </div>
                    )}

                    {include === "allFieldsExcept" && (
                      <div className="space-y-1 mt-2">
                        <label className="text-[10px] text-muted-foreground">
                          Fields to exclude (comma-separated or drag fields)
                        </label>
                        <div
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            e.currentTarget.classList.add(
                              "ring-2",
                              "ring-primary",
                              "bg-primary/5",
                            );
                          }}
                          onDragLeave={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            e.currentTarget.classList.remove(
                              "ring-2",
                              "ring-primary",
                              "bg-primary/5",
                            );
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            e.currentTarget.classList.remove(
                              "ring-2",
                              "ring-primary",
                              "bg-primary/5",
                            );
                            const dragData =
                              e.dataTransfer.getData("application/json");
                            if (dragData) {
                              try {
                                const parsedData = JSON.parse(dragData);
                                const fieldPath = parsedData.path;
                                if (fieldPath) {
                                  let cleanPath = fieldPath
                                    .replace(/^\{\{\s*/, "")
                                    .replace(/\s*\}\}$/, "");
                                  cleanPath = cleanPath
                                    .replace(/^\$json\./, "")
                                    .replace(/^config\./, "");
                                  const pathParts = cleanPath.split(".");
                                  const fieldName = pathParts[
                                    pathParts.length - 1
                                  ].replace(/\[\d+\]/g, "");
                                  const existingFields = fieldsToExclude
                                    .split(",")
                                    .map((f) => f.trim())
                                    .filter((f) => f.length > 0);
                                  if (!existingFields.includes(fieldName)) {
                                    existingFields.push(fieldName);
                                  }
                                  setFieldsToExclude(existingFields.join(", "));
                                }
                              } catch (error) {
                                console.error(
                                  "Error parsing drag data:",
                                  error,
                                );
                              }
                            }
                          }}
                          className="w-full border border-transparent rounded transition-all"
                        >
                          <Input
                            value={fieldsToExclude}
                            onChange={(e) => setFieldsToExclude(e.target.value)}
                            placeholder="password,token,secret"
                            className="text-xs h-7 font-mono"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Options Section */}
                  <div className="space-y-2 border rounded-md p-3 bg-muted/20">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-semibold">Options</label>
                      <div className="relative">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() =>
                            setShowOptionsDropdown(!showOptionsDropdown)
                          }
                        >
                          <Plus size={14} />
                        </Button>

                        {showOptionsDropdown && (
                          <div className="absolute right-0 top-8 z-10 bg-background border shadow-lg rounded-md py-1 min-w-[200px]">
                            <div
                              className={`px-3 py-2 text-xs cursor-pointer ${
                                options.keepMissing !== undefined
                                  ? "opacity-50 cursor-not-allowed"
                                  : "hover:bg-accent"
                              }`}
                              onClick={() => {
                                if (options.keepMissing === undefined)
                                  addOption("keepMissing");
                              }}
                            >
                              Keep Missing
                              {options.keepMissing !== undefined && (
                                <span className="ml-2 text-muted-foreground">
                                  (Added)
                                </span>
                              )}
                            </div>
                            <div
                              className={`px-3 py-2 text-xs cursor-pointer ${
                                options.mergeLists !== undefined
                                  ? "opacity-50 cursor-not-allowed"
                                  : "hover:bg-accent"
                              }`}
                              onClick={() => {
                                if (options.mergeLists === undefined)
                                  addOption("mergeLists");
                              }}
                            >
                              Merge Lists
                              {options.mergeLists !== undefined && (
                                <span className="ml-2 text-muted-foreground">
                                  (Added)
                                </span>
                              )}
                            </div>
                            <div
                              className={`px-3 py-2 text-xs cursor-pointer ${
                                options.disableDotNotation !== undefined
                                  ? "opacity-50 cursor-not-allowed"
                                  : "hover:bg-accent"
                              }`}
                              onClick={() => {
                                if (options.disableDotNotation === undefined)
                                  addOption("disableDotNotation");
                              }}
                            >
                              Disable Dot Notation
                              {options.disableDotNotation !== undefined && (
                                <span className="ml-2 text-muted-foreground">
                                  (Added)
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      {options.keepMissing !== undefined && (
                        <div className="flex items-center justify-between p-2 bg-background border rounded text-xs">
                          <span className="flex-1">Keep Missing</span>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={options.keepMissing}
                              onCheckedChange={(checked) =>
                                setOptions({ ...options, keepMissing: checked })
                              }
                            />
                            <Trash2
                              size={14}
                              className="cursor-pointer text-red-500 hover:text-red-700 transition"
                              onClick={() => removeOption("keepMissing")}
                            />
                          </div>
                        </div>
                      )}

                      {options.mergeLists !== undefined && (
                        <div className="flex items-center justify-between p-2 bg-background border rounded text-xs">
                          <span className="flex-1">Merge Lists</span>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={options.mergeLists}
                              onCheckedChange={(checked) =>
                                setOptions({ ...options, mergeLists: checked })
                              }
                            />
                            <Trash2
                              size={14}
                              className="cursor-pointer text-red-500 hover:text-red-700 transition"
                              onClick={() => removeOption("mergeLists")}
                            />
                          </div>
                        </div>
                      )}

                      {options.disableDotNotation !== undefined && (
                        <div className="flex items-center justify-between p-2 bg-background border rounded text-xs">
                          <span className="flex-1">Disable Dot Notation</span>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={options.disableDotNotation}
                              onCheckedChange={(checked) =>
                                setOptions({
                                  ...options,
                                  disableDotNotation: checked,
                                })
                              }
                            />
                            <Trash2
                              size={14}
                              className="cursor-pointer text-red-500 hover:text-red-700 transition"
                              onClick={() => removeOption("disableDotNotation")}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {/* End scroll wrapper */}

                {/* Execute Button — sticky at bottom, outside the scroll wrapper
                  so it always stays visible regardless of scroll position */}
                <div className="flex-shrink-0 pt-2 border-t bg-background">
                  <Button
                    onClick={executeAggregate}
                    disabled={
                      !inputData ||
                      inputData.length === 0 ||
                      !isTitleValid ||
                      loading ||
                      !title.trim()
                    }
                    className="w-full"
                    size="sm"
                  >
                    <FlaskConical size={14} className="mr-2" />
                    Execute step
                  </Button>
                </div>
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(1)} />

            {/* Right Section - Response (4/12 columns) */}
            <div style={{ flex: 1, minWidth: 80 }}>
              <NodeContentSection scrollable={false} hasRelative={true}>
                <ResponseViewer
                  response={errorResponse || outputData}
                  loading={loading}
                  emptyMessage="Execute aggregate to see output preview"
                  loadingMessage="Processing..."
                  onCopy={handleCopyOutput}
                  isError={!!errorResponse}
                  isLoopChild={(data as any).is_loop_child || false}
                  totalIterations={(data as any).total_iterations || 0}
                  iterations={(data as any).iterations || []}
                  fillParent={true}
                  onResponseChange={(newData) => {
                    setOutputData(newData);
                    data.onDataChange?.(id, {
                      response: newData,
                      manuallyEdited: true,
                    });
                  }}
                />
              </NodeContentSection>
            </div>
          </div>
        </div>
      )}
    </FullscreenWrapper>
  );
};

export default AggregateNode;
